/**
 * One entry in the codex, and the three things an entry can be.
 *
 * These lived in `WorldPanel.tsx` until the "here, now" block arrived and needed to draw a
 * person the same way the people list does. That is the whole reason for the file: **an NPC
 * standing in front of you and an NPC three sections down are the same card**, and the moment
 * they are two components they start drifting a badge at a time — which is exactly the drift
 * `Scene.tsx` exists to prevent on the other side of the app.
 *
 * The panel keeps the sections and the joins; this keeps the shapes.
 */
import { useState } from 'react';
import type { Relationship, WorldFaction, WorldLocation, WorldNpc } from '@/api/generated';
import { codexElementId } from '@/features/world/codexAnchor';
import {
  heldBy,
  lastChapter,
  npcStatus,
  RELATIONSHIP_AXIS,
  relationship,
  relationshipStep,
  reputationLabel,
  toneColor,
  visitState,
  type WorldTone,
} from '@/features/world/world';
import { useT } from '@/i18n/context';

export function Place({
  place,
  factionNames,
  targeted = false,
}: {
  place: WorldLocation;
  factionNames: ReadonlyMap<string, string>;
  targeted?: boolean;
}) {
  const t = useT();
  const state = visitState(place.state);

  return (
    <Entry
      id={codexElementId('location', place.name)}
      targeted={targeted}
      name={place.display_name}
      badge={t(state.label)}
      tone={state.tone}
      // `last_visited_chapter` is 0 for somewhere heard about and never reached, which is a
      // meaningfully different thing from "visited in chapter 0" and reads as such.
      meta={
        place.last_visited_chapter > 0
          ? t('ui.world.chapter_ref', { chapter: place.last_visited_chapter })
          : t('ui.world.never')
      }
      description={place.description ?? place.short_description}
      facts={place.factions.length > 0 ? [heldBy(t, place, factionNames)] : []}
    />
  );
}

export function Faction({
  faction,
  targeted = false,
}: {
  faction: WorldFaction;
  targeted?: boolean;
}) {
  const t = useT();
  const standing = relationship(faction.relationship);
  const seen = lastChapter(faction.interaction_chapters);

  return (
    <Entry
      id={codexElementId('faction', faction.name)}
      targeted={targeted}
      name={faction.display_name}
      badge={`${t(standing.label)} ${reputationLabel(faction.reputation)}`}
      tone={standing.tone}
      scale={faction.relationship}
      meta={
        seen === null ? t('ui.world.not_dealt_with') : t('ui.world.chapter_ref', { chapter: seen })
      }
      description={faction.description ?? faction.short_description}
      facts={faction.permanent_facts}
    />
  );
}

export function Npc({
  npc,
  placeNames,
  id,
  targeted = false,
}: {
  npc: WorldNpc;
  /**
   * Omitted by the "here, now" block, which already says where you are: repeating *last known
   * at Rust Market* under a person standing in the Rust Market is the caption telling you what
   * the heading just told you.
   */
  placeNames?: ReadonlyMap<string, string>;
  /**
   * The codex anchor, and **the people section mints it while `HereNow` does not** — the same
   * person is drawn twice on an open panel, and a duplicated DOM id makes `getElementById`
   * a coin flip (`codexAnchor.ts`).
   */
  id?: string;
  targeted?: boolean;
}) {
  const t = useT();
  const standing = relationship(npc.relationship);
  const status = npcStatus(npc.status);
  const seen = lastChapter(npc.appearance_chapters);
  const where = npc.current_location;

  return (
    <Entry
      id={id}
      targeted={targeted}
      name={npc.display_name}
      // Status wins the badge when there is one: that Delacroix is *missing* outranks the
      // fact that he was unfriendly about it.
      badge={status ? `${t(standing.label)} · ${t(status.label)}` : t(standing.label)}
      tone={status?.tone ?? standing.tone}
      // The card's tone can be the *status*'s — a dead NPC is drawn in alarm whatever they
      // thought of you — so the scale takes the relationship itself and colours from that.
      scale={npc.relationship}
      meta={seen === null ? t('ui.world.never_seen') : t('ui.world.chapter_ref', { chapter: seen })}
      description={npc.description}
      facts={[
        ...(placeNames !== undefined && where !== null
          ? [t('ui.world.last_known_at', { place: placeNames.get(where) ?? where })]
          : []),
        ...npc.permanent_facts,
      ]}
    />
  );
}

/**
 * One card, collapsed. The summary carries the two things worth scanning — what it is called
 * and how it stands toward you — and the body carries the prose the DM wrote.
 *
 * Same `<details>` argument as everywhere else in these panels: a keyboard reaches it, a
 * screen reader announces it, and Ctrl-F finds the text inside it while it is shut, which for
 * a codex that grows all story is the property that matters most.
 */
export function Entry({
  id,
  targeted = false,
  name,
  badge,
  tone,
  scale,
  meta,
  description,
  facts,
}: {
  /**
   * The codex anchor a mention scrolls to (U6). Optional because `HereNow` draws the same
   * cards without one — see `Npc`'s `id` above.
   */
  id?: string;
  /** This is the card a mention pointed at: expand it and take focus. */
  targeted?: boolean;
  name: string;
  badge: string;
  tone: WorldTone;
  /**
   * The hostile→allied axis, for the two kinds of entry that stand somewhere on it. Places
   * do not, and pass nothing: a codex where a storm drain has an opinion of you is worse than
   * one where the column is ragged.
   */
  scale?: Relationship;
  meta: string;
  description: string | null;
  facts: readonly string[];
}) {
  const t = useT();
  const hasBody = (description !== null && description.length > 0) || facts.length > 0;
  // Same shape as `ui/Section.tsx`'s `reveal`, down to being compared during render rather
  // than in an effect: false→true opens it, and the player can shut it again. A disclosure
  // permanently forced open by a prop is not a disclosure.
  // Seeded rather than false, for the reason `ui/Section.tsx` says: the drawer mounts on
  // open, so a targeted card has no false→true edge for the comparison below to catch.
  const [open, setOpen] = useState(targeted);
  const [wasTargeted, setWasTargeted] = useState(targeted);
  if (targeted !== wasTargeted) {
    setWasTargeted(targeted);
    if (targeted) setOpen(true);
  }

  const head = (
    <>
      <span className="text-fg-muted truncate">{name}</span>
      {/*
        The badge and the scale travel together at the right edge, scale outermost. That order
        is the whole reason the scale is worth drawing: the words are ragged — *hostile −41* and
        *unfriendly · missing* are different lengths — so a picture placed inside them would be
        ragged too, and a scale you have to align by eye down a list of twelve is a scale that
        has not been drawn on.
      */}
      <span className="ml-auto flex shrink-0 items-baseline gap-2">
        <span className="text-fg-faint tabular-nums" style={{ color: toneColor(tone) }}>
          {badge}
        </span>
        {scale !== undefined ? <RelationshipScale value={scale} /> : null}
      </span>
    </>
  );

  if (!hasBody) {
    return (
      <div
        id={id}
        // Focusable only programmatically: the card is where a mention lands the reader, and
        // `Dialog`'s focus restore then returns them to the word they tapped on close.
        tabIndex={-1}
        className="flex items-baseline gap-2 py-1 font-mono outline-none"
        style={{ fontSize: 'var(--text-meta)' }}
      >
        {head}
      </div>
    );
  }

  return (
    <details
      id={id}
      tabIndex={-1}
      open={open}
      className="outline-none"
      onToggle={(event) => {
        setOpen(event.currentTarget.open);
      }}
    >
      <summary
        className="hover:text-fg flex cursor-pointer list-none items-baseline gap-2 py-1 font-mono transition-colors"
        style={{ fontSize: 'var(--text-meta)' }}
      >
        {head}
      </summary>
      <div className="border-rule mt-1 mb-2 ml-1 flex flex-col gap-2 border-l pl-3">
        {description !== null && description.length > 0 ? (
          <p className="text-fg-muted" style={{ fontSize: 'var(--text-meta)' }}>
            {description}
          </p>
        ) : null}
        {facts.map((fact) => (
          <p key={fact} className="text-fg-faint" style={{ fontSize: 'var(--text-meta)' }}>
            {fact}
          </p>
        ))}
        <p className="text-fg-faint font-mono" style={{ fontSize: 'var(--text-meta)' }}>
          {t('ui.world.last', { value: meta })}
        </p>
      </div>
    </details>
  );
}

/**
 * The hostile→allied axis, drawn (`TODO.md` §4.6).
 *
 * Five cells with neutral in the middle, filled from the middle outward to wherever this
 * relationship sits: **the side says which way and the length says how far**, and both are
 * legible before a word is. That is what the item was asking for — it named the "scale
 * factions already get", and the answer was that neither factions nor people had one. A
 * faction's `reputation` could not be it either: people carry no number, so the only axis both
 * halves of the codex can be drawn on is the enum they share.
 *
 * **The colour is `world.ts`'s judgement and not a second one.** `unfriendly` and `neutral`
 * are `plain` there, so they stay uncoloured here and fall back to faint — the alternative
 * was an amber this file would have been inventing for a state the rest of the codex has
 * decided is not news. Only the two ends get colour, as everywhere else in this panel.
 *
 * Decorative on purpose. The word is one span to its left, in the player's language and in the
 * story's; a `meter` here would make every card announce its standing twice.
 */
function RelationshipScale({ value }: { value: Relationship }) {
  const step = relationshipStep(value);
  const lit = toneColor(relationship(value).tone) ?? 'var(--color-fg-faint)';

  return (
    <span aria-hidden="true" className="rel-scale">
      {RELATIONSHIP_AXIS.map((cell) => (
        <span
          key={cell}
          style={{
            // Between the origin and the step, inclusive — so neutral is always the one lit
            // cell in the middle and never an empty row that reads as missing data.
            backgroundColor:
              cell >= Math.min(0, step) && cell <= Math.max(0, step) ? lit : 'var(--color-rule)',
          }}
        />
      ))}
    </span>
  );
}
